import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip,
  Menu,
  MenuTrigger,
  useOverflowMenu,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  partitionBreadcrumbItems,
  truncateBreadcrumbLongName,
  isTruncatableBreadcrumbContent,
} from '@fluentui/react-breadcrumb';
import { MoreHorizontalRegular } from '@fluentui/react-icons';

const DefaultMaxDisplayedItems = 5;
const DefaultMaxLengthOfItem = 30;

const OverflowItemsMenu = ({
  overflowItems,
  onItemClick,
}) => {
  const { ref, isOverflowing, overflowCount } = useOverflowMenu();
  // const tooltipStyles = useTooltipStyles();

  const overflowItemsCount = useMemo(() =>
    (overflowItems ? overflowItems.length + overflowCount : overflowCount
    ), [overflowItems, overflowCount]);

  if (!isOverflowing && overflowItems && overflowItems.length === 0) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          id="menu"
          appearance="subtle"
          ref={ref}
          icon={<MoreHorizontalRegular />}
          aria-label={`${overflowItemsCount} more items`}
          role="button"
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {
            overflowItems && overflowItems.map(item => (
              <MenuItem key={item.key} onClick={() => onItemClick(item)}>{item.text}</MenuItem>
            ))
          }
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export const AdsStudioBreadcrumb = ({
  items,
  onItemClick,
  maxDisplayedItems,
  maxLengthOfItem,
  customItemStyle,
}) => {
  const {
    startDisplayedItems,
    overflowItems,
    endDisplayedItems,
  } = partitionBreadcrumbItems({
    items,
    maxDisplayedItems,
  });

  const lastIndex = useMemo(() => items.length - 1, [items]);

  const renderItem = useCallback((entry, isLastItem, onClick) => (
    <React.Fragment key={`item-${entry.key}`}>
      {isTruncatableBreadcrumbContent(entry.text, maxLengthOfItem) ? (
        <BreadcrumbItem>
          <Tooltip withArrow content={entry.text} relationship="label">
            <BreadcrumbButton
              current={isLastItem}
              onClick={() => onClick(entry)}
              className={mergeClasses(customItemStyle.breadcrumbButton, isLastItem && customItemStyle.breadcrumbButtonCurrent)}
            >
              {truncateBreadcrumbLongName(entry.text, maxLengthOfItem)}
            </BreadcrumbButton>
          </Tooltip>
        </BreadcrumbItem>
        ) : (
          <BreadcrumbItem>
            <BreadcrumbButton
              current={isLastItem}
              onClick={() => onClick(entry)}
              className={mergeClasses(customItemStyle.breadcrumbButton, isLastItem && customItemStyle.breadcrumbButtonCurrent)}
            >{entry.text}
            </BreadcrumbButton>
          </BreadcrumbItem>
        )}

      {!isLastItem && <BreadcrumbDivider />}
    </React.Fragment>
  ), [customItemStyle, maxLengthOfItem]);

  return (
    <Breadcrumb>
      {
        startDisplayedItems && startDisplayedItems.map((item, index) => renderItem(item, index === lastIndex, onItemClick))
      }
      {
        overflowItems &&
          <OverflowItemsMenu
            overflowItems={overflowItems}
            onItemClick={onItemClick}
          />
      }
      {
        endDisplayedItems && endDisplayedItems.map((item, index) => {
          const isLastItem = index + (items.length - endDisplayedItems.length) === lastIndex;
          return renderItem(item, isLastItem, onItemClick);
        })
      }
    </Breadcrumb>
  );
};

AdsStudioBreadcrumb.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    text: PropTypes.string,
  })).isRequired,
  onItemClick: PropTypes.func.isRequired,
  maxDisplayedItems: PropTypes.number,
  maxLengthOfItem: PropTypes.number,
  customItemStyle: PropTypes.shape({
    breadcrumbButton: PropTypes.string,
    breadcrumbButtonCurrent: PropTypes.string,
  }),
};

AdsStudioBreadcrumb.defaultProps = {
  maxDisplayedItems: DefaultMaxDisplayedItems,
  maxLengthOfItem: DefaultMaxLengthOfItem,
  customItemStyle: {},
};
